import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useSelector } from "react-redux";
import { AppState, useAppDispatch } from "../../stores/CommunityStore";
import { fetchPosts } from "../../stores/CommunityStore/posts/actions/fetchPosts";
import { fetchTopics } from "../../stores/CommunityStore/topics/actions/fetchTopics";
import { fetchTrendingPosts } from "../../stores/CommunityStore/trendingPosts/actions/fetchTrendingPosts";
import { CurrentUser } from "../../stores/CurrentUserStore/Types";
import PricingStore from "../../stores/PricingStore";
import Hero from "./components/Hero";
import PermissionGateModal from "./components/PermissionGateModal";
import Shell from "./Shell";
import Icon from "./Icon";

interface CommunityNewProps {
  currentUser: CurrentUser;
  currentUserLoaded: boolean;
  getCurrentUser: () => Promise<void>;
  pricingStore: PricingStore;
}

interface NotificationTarget {
  postId?: string;
  replyId?: string;
}

const getNotificationTarget = (): NotificationTarget => {
  const storedTarget = sessionStorage.getItem("communityNotificationTarget");

  if (storedTarget) {
    sessionStorage.removeItem("communityNotificationTarget");
    return JSON.parse(storedTarget);
  }

  const params = new URLSearchParams(window.location.search);
  const postId = params.get("post_id") || undefined;
  const replyId =
    params.get("reply_comment_id") || params.get("comment_id") || undefined;

  if (postId || replyId) {
    window.history.replaceState({}, "", window.location.pathname);
  }

  return { postId, replyId };
};

const CommunityNew = ({
  currentUser,
  currentUserLoaded,
  getCurrentUser,
  pricingStore,
}: CommunityNewProps) => {
  const dispatch = useAppDispatch();
  const { posts, loading } = useSelector((state: AppState) => state.posts);
  const { topics } = useSelector((state: AppState) => state.topics);
  const featuredPosts = useSelector(
    (state: AppState) => state.trendingPosts.trendingPosts
  );
  const { tools, loading: toolsLoading } = useSelector(
    (state: AppState) => state.tools
  );
  const [notificationTarget] = useState(getNotificationTarget);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isGated = currentUserLoaded && !currentUser?.can_access_community;
  const pricingState = pricingStore.pricingState;
  const communityPricing =
    pricingState.kind === "ready" && pricingState.pricing.kind === "pricing"
      ? pricingState.pricing.community_pricing
      : undefined;

  useEffect(() => {
    dispatch(fetchTopics());
    if (notificationTarget.postId) {
      dispatch(fetchPosts({ featuredPostId: notificationTarget.postId }));
    }
    dispatch(fetchTrendingPosts());
  }, [dispatch, notificationTarget.postId]);

  const closeAccessModal = () => {
    window.location.href = "/";
  };

  return (
    <>
      <Icon />
      <div className="ui pages" id="community-new">
        <Hero
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />
        <Shell
          isSidebarOpen={isSidebarOpen}
          onCloseSidebar={() => setIsSidebarOpen(false)}
          currentUser={currentUser}
          posts={posts}
          postsLoading={loading}
          topics={topics}
          featuredPosts={featuredPosts}
          tools={tools}
          toolsLoading={toolsLoading}
          getCurrentUser={getCurrentUser}
          targetReplyId={notificationTarget.replyId}
          loadInitialTopicPosts={!notificationTarget.postId}
        />
        <PermissionGateModal
          isOpen={isGated}
          closeModal={closeAccessModal}
          isLoggedIn={currentUser.kind === "current-user"}
          communityPricing={communityPricing}
        />
      </div>
    </>
  );
};

export default observer(CommunityNew);
